import {ApiResponseInterface} from '@/common/http/http.interface';
import {SyncSendHeldQueuedInterface} from './sync-health.interface';

/**
 * The reasons send-held gives for sending nothing (task 4.7): each has its own words
 */
const SEND_REFUSALS = ['productive_archived', 'not_linked', 'productive_no_membership', 'productive_not_found', 'productive_unavailable'];

/**
 * What a send of the held tickets came to, as a translation key with its values. The health view's held tab and the
 * alert banner say it in the same words (tasks 4.9, 4.10).
 */
export interface SendHeldResult {
	key: string;
	params?: Record<string, string>;
}

/**
 * The words for send-held's answer: the queued count, and the tickets another send already holds (their claims),
 * which the send leaves out
 */
export function sendHeldAnswer(response: ApiResponseInterface): SendHeldResult {
	const answer = queuedAnswer(response);
	// A 403 and a 422 arrive as answers with success false (auth.interceptor, errors.interceptor)
	if (answer === null) {
		return {key: 'sync.held.sendFailed'};
	}
	const claimed = answer.claimed_ids?.length ?? 0;
	if (answer.queued > 0 && claimed > 0) {
		return {key: 'sync.held.queuedSome', params: {count: String(answer.queued), claimed: String(claimed)}};
	}
	if (answer.queued > 0) {
		return {key: 'sync.held.queued', params: {count: String(answer.queued)}};
	}

	return {key: claimed > 0 ? 'sync.held.alreadyQueued' : 'sync.held.nothingHeld'};
}

/**
 * Whether the answer leaves tickets on their way: queued by this send, or by another send that claimed them. They stay
 * held until their jobs run.
 */
export function sendHeldStarted(response: ApiResponseInterface): boolean {
	const answer = queuedAnswer(response);

	return answer !== null && (answer.queued > 0 || (answer.claimed_ids?.length ?? 0) > 0);
}

/**
 * The words for an error from send-held: each 409 reason and the 503 from `data.reason` (HttpService.handleError passes
 * it on), else "the send could not be started"
 */
export function sendHeldRefusal(error: ApiResponseInterface): SendHeldResult {
	const reason = (error?.data as {reason?: unknown} | undefined)?.reason;

	return typeof reason === 'string' && SEND_REFUSALS.includes(reason)
		? {key: `sync.held.refused.${reason}`}
		: {key: 'sync.held.sendFailed'};
}

/**
 * Send-held's record of what it queued, or null for a refusal that arrived as an answer, or an answer without one
 */
function queuedAnswer(response: ApiResponseInterface): SyncSendHeldQueuedInterface | null {
	const answer = response.data as SyncSendHeldQueuedInterface | null | undefined;

	return response.success && typeof answer === 'object' && answer !== null ? answer : null;
}
